// The first-login product tour: what each stop says and where it points. Pure data + helpers,
// no React (tested in tourSteps.test.js). A stop whose anchor is not on screen (a feature the
// org doesn't have, Settings for a non-admin) is dropped, never shown pointing at nothing.

/** `[data-tour="…"]` anchor for a sidebar item, by its route. */
export const navAnchor = (path) => `nav-${path}`;

export const TOUR_STEPS = [
  {
    id: 'welcome',
    kind: 'intro',
    title: 'Welcome to VerOps',
    body: 'A one-minute tour of where everything lives. You can leave at any point and restart it from your profile menu.',
  },
  {
    id: 'sidebar',
    anchor: 'sidebar',
    placement: 'right',
    eyebrow: 'Navigation',
    title: 'Everything starts in this sidebar',
    body: 'It is grouped by what you are doing: Monitor what is running, Analyze what happened, ask AI, and Manage your workspace.',
    hint: 'Collapse it with the arrow at the bottom when you need room.',
  },
  {
    id: 'home',
    anchor: navAnchor('/'),
    eyebrow: 'Overview',
    title: 'Home: your morning check',
    body: 'The health of every application in one place: what needs attention now, and the services you chose to watch.',
    path: '/',
  },
  {
    id: 'apm',
    anchor: navAnchor('/applications'),
    eyebrow: 'Monitor',
    title: 'Applications (APM)',
    body: 'Every service, operation and trace. Open an operation to see throughput, latency and errors, then the requests behind them.',
    hint: 'Drag across any chart to zoom the whole page to that window.',
    path: '/applications',
  },
  {
    id: 'infra',
    anchor: navAnchor('/infrastructure'),
    eyebrow: 'Monitor',
    title: 'Infrastructure',
    body: 'Servers and machine agents: CPU, memory, disks and processes, with each agent’s heartbeat and logs.',
    path: '/infrastructure',
  },
  {
    id: 'alerts',
    anchor: navAnchor('/alerts'),
    eyebrow: 'Monitor',
    title: 'Alerts',
    body: 'Get told when something crosses a line. Rules watch your metrics, logs and checks; routing and on-call live under Response.',
    path: '/alerts',
  },
  {
    id: 'synthetics',
    anchor: navAnchor('/synthetic'),
    eyebrow: 'Analyze',
    title: 'Synthetic monitoring',
    body: 'Scheduled checks of your sites, APIs and user journeys. See availability, run times, and exactly which step broke.',
    path: '/synthetic',
  },
  {
    id: 'dashboards',
    anchor: navAnchor('/dashboards'),
    eyebrow: 'Overview',
    title: 'Dashboards',
    body: 'Start from the ready-made dashboards or build your own from any metric, log or trace.',
    path: '/dashboards',
  },
  {
    id: 'explorer',
    anchor: navAnchor('/data-explorer'),
    eyebrow: 'Analyze',
    title: 'Data Explorer',
    body: 'Query your metrics, logs and traces directly when you need an answer no dashboard has yet.',
    path: '/data-explorer',
  },
  {
    id: 'vera',
    anchor: navAnchor('/vera'),
    eyebrow: 'AI',
    title: 'Ask Vera',
    body: 'Ask in plain English, like “which host used the most CPU yesterday?”, and Vera answers from your own data.',
    path: '/vera',
  },
  {
    id: 'settings',
    anchor: navAnchor('/settings'),
    eyebrow: 'Manage',
    title: 'Settings',
    body: 'Invite your team, set roles, create API keys and connect notification channels.',
    path: '/settings?tab=users',
  },
  {
    id: 'profile',
    anchor: 'profile',
    placement: 'right-end',
    eyebrow: 'Your account',
    title: 'Profile and this tour',
    body: 'Your profile, security and sign-out live here, along with “Product tour” whenever you want this walkthrough again.',
  },
  {
    id: 'done',
    kind: 'outro',
    title: 'You’re all set',
    body: 'Here are the quickest ways to see your own data in VerOps.',
  },
];

/** Where to go first, shown on the last card. Admin-only actions are dropped for others. */
export const QUICK_STARTS = [
  { id: 'agent', title: 'Connect your first system', body: 'Install an agent or send OpenTelemetry data.', path: '/onboarding' },
  { id: 'test', title: 'Create a synthetic test', body: 'Watch a URL or API from outside in two minutes.', path: '/synthetic/tests/create' },
  { id: 'team', title: 'Invite your team', body: 'Add people and choose what they can see.', path: '/settings?tab=users', adminOnly: true },
  { id: 'docs', title: 'Read the docs', body: 'Guides for every feature at verops.io/docs.', href: 'https://verops.io/docs' },
];

/**
 * The stops to show: intro and outro always, an anchored stop only when its anchor is present.
 * `hasAnchor(name)` answers for the current DOM (injected so this stays pure).
 */
export function visibleSteps(steps, hasAnchor) {
  return steps.filter((s) => s.kind === 'intro' || s.kind === 'outro' || hasAnchor(s.anchor));
}

export const quickStartsFor = (isAdmin) => QUICK_STARTS.filter((q) => !q.adminOnly || isAdmin);

/**
 * Where the card sits relative to the highlighted rect, kept inside the viewport.
 * Returns {left, top} for a card of size {w, h}; `gap` px from the target.
 */
export function cardPosition(rect, card, viewport, placement = 'right', gap = 18) {
  const margin = 16;
  let left = rect.left + rect.width + gap;
  let top = placement === 'right-end' ? rect.top + rect.height - card.h : rect.top + rect.height / 2 - card.h / 2;
  if (left + card.w > viewport.w - margin) left = Math.max(margin, rect.left - gap - card.w);
  top = Math.min(Math.max(margin, top), viewport.h - card.h - margin);
  return { left: Math.round(left), top: Math.round(top) };
}
